import type {
  HTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';
import { ChevronDown, ChevronUp } from './icons';

/* ────────────────────────────────────────────────────────────────────────────
   Table wrappers.

   Hairline rows, a sticky glass header, and right-aligned monospaced numerals —
   the layout a trading desk expects. Wrap wide tables in `.scroll-x`.
   ──────────────────────────────────────────────────────────────────────────── */

export type CellAlign = 'left' | 'center' | 'right';

const ALIGN: Record<CellAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export function Table({ className, children, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return (
    <table
      className={cn('w-full border-separate border-spacing-0 text-sm', className)}
      {...rest}
    >
      {children}
    </table>
  );
}

export function THead({ className, children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn('sticky top-0 z-10 bg-surface/95 backdrop-blur', className)}
      {...rest}
    >
      {children}
    </thead>
  );
}

export function TBody({ className, children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...rest}>
      {children}
    </tbody>
  );
}

export interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Adds pointer affordance for rows that navigate somewhere. */
  interactive?: boolean;
}

export function TR({ className, interactive, children, ...rest }: TRProps) {
  return (
    <tr
      className={cn(
        'group/row transition-colors duration-150 ease-swift hover:bg-elevated/60',
        interactive && 'cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

export interface THProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> {
  sortable?: boolean;
  active?: boolean;
  dir?: 'asc' | 'desc';
  onSort?: () => void;
  align?: CellAlign;
}

export function TH({
  sortable = false,
  active = false,
  dir = 'desc',
  onSort,
  align = 'left',
  className,
  children,
  ...rest
}: THProps) {
  const Chevron = dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th
      scope="col"
      aria-sort={sortable ? (active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
      className={cn(
        'whitespace-nowrap border-b border-line px-3 py-2 text-3xs font-medium uppercase tracking-[0.14em] text-subtle',
        ALIGN[align],
        className,
      )}
      {...rest}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            // The negative margin makes the button fill its cell's padding, so
            // the pointer target is the whole header cell rather than the ~12px
            // text box (WCAG 2.5.8 wants 24px).
            '-mx-3 -my-2 inline-flex min-h-6 items-center gap-1 rounded px-3 py-2 uppercase tracking-[0.14em] transition-colors duration-150 ease-swift hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70',
            align === 'right' && 'flex-row-reverse',
            active && 'text-fg',
          )}
        >
          {children}
          <Chevron
            size={11}
            className={cn('shrink-0 transition-opacity', active ? 'opacity-100' : 'opacity-35')}
          />
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export interface TDProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> {
  align?: CellAlign;
  /** Force the monospaced numeric treatment; `align="right"` implies it. */
  numeric?: boolean;
}

export function TD({ align = 'left', numeric, className, children, ...rest }: TDProps) {
  const mono = numeric ?? align === 'right';
  return (
    <td
      className={cn(
        'border-b border-line/70 px-3 py-2.5 text-xs text-fg',
        ALIGN[align],
        mono && 'font-mono tabular-nums',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
